import { useEffect, useState } from 'react'

/**
 * useMediaQuery — subscribes to a CSS media query and returns its current match state.
 *
 * SSR-safe: returns `false` until mounted (no `window` in Node).
 * Layout shifts on first paint are acceptable because the consuming component
 * decides what to render in either branch.
 *
 * Phase-1 mobile rollout: breakpoints centralized in BREAKPOINTS export.
 * Convenience helpers (useIsPhone / useIsTablet / useIsTouch) wrap common queries.
 *
 * @param {string} query - any valid media query string, e.g. "(max-width: 480px)"
 * @returns {boolean}
 */
export default function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false
        return window.matchMedia(query).matches
    })

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined
        const mql = window.matchMedia(query)
        const onChange = (e) => setMatches(e.matches)
        setMatches(mql.matches)
        if (mql.addEventListener) {
            mql.addEventListener('change', onChange)
            return () => mql.removeEventListener('change', onChange)
        }
        mql.addListener(onChange)
        return () => mql.removeListener(onChange)
    }, [query])

    return matches
}

export const BREAKPOINTS = Object.freeze({
    phone: 480,
    tablet: 768,
    laptop: 1024,
    desktop: 1280,
})

export function useIsPhone() {
    return useMediaQuery(`(max-width: ${BREAKPOINTS.phone}px)`)
}

export function useIsTablet() {
    return useMediaQuery(`(max-width: ${BREAKPOINTS.tablet}px)`)
}

export function useIsTouch() {
    return useMediaQuery('(hover: none) and (pointer: coarse)')
}

export function useIsStandalonePwa() {
    return useMediaQuery('(display-mode: standalone)')
}

/**
 * `true` when the layout collapses to a mobile shell (burger menu visible).
 * Mirror of the `@media (max-width: 1024px)` block in `src/styles/index.css`.
 */
export function useIsMobileLayout() {
    return useMediaQuery(`(max-width: ${BREAKPOINTS.laptop}px)`)
}
